import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { CatalogImageOwner } from "../../../features/floorplan3d/catalog/catalogQueries";
import { getCatalogItemDisplayName } from "../../../features/floorplan3d/catalog/catalogDisplay";
import { normalizeCatalogProductDimensions } from "../../../features/floorplan3d/products/productDimensionUtils";
import { convertWorldToMillimeter } from "../../../utils/conversions";
import { productImages } from "./filterUtils";
import { floorplannerStore } from "../../../store/floorplannerStore";
import { useCurrencyRates } from "../../../hooks/useCurrencyRates";
import { formatDisplayPrice } from "../../../utils/currency";

type Props = {
  product: CatalogImageOwner;
  onClose: () => void;
};

const featureList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item)))
      .filter(Boolean);
  }
  if (typeof value === "string" && value && value !== "N/A") return [value];
  return [];
};

const millimeterDimensionValue = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? `${convertWorldToMillimeter(value, 1).toLocaleString()} mm`
    : "--";

/**
 * Client-matched product detail modal. Informational only — insert stays on
 * the card click. Overlay clicks close this dialog, not the library.
 */
export const ProductLibraryDetailModal: React.FC<Props> = observer(({
  product,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const { ratesArray, base } = useCurrencyRates();
  const displayCurrency = floorplannerStore.currency;
  const images = productImages(product);
  const [index, setIndex] = React.useState(0);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const displayName = getCatalogItemDisplayName(product);
  const materials = featureList(product.features3?.material);
  const modelDimensions = product.active3DAsset?.dimensions;
  const feedDimensions = normalizeCatalogProductDimensions(product).dimensions;

  React.useEffect(() => setIndex(0), [product.id]);

  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", handleKey, true);
    };
  }, [onClose]);

  const current = images[index];

  return createPortal(
    <div
      data-product-library-modal="true"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-3 sm:p-6"
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={displayName}
        onMouseDown={(event) => event.stopPropagation()}
        className="relative flex max-h-[calc(100vh-32px)] w-full max-w-6xl overflow-hidden rounded-2xl bg-[#F8F7F5] shadow-2xl"
      >
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
          aria-label={t("productLibrary.closeProductDetails")}
          className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#111] shadow-sm ring-1 ring-black/5 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
        >
          <IoClose size={20} />
        </button>
        <div className="grid min-h-0 w-full grid-cols-1 lg:grid-cols-2">
          <section className="flex min-h-0 flex-col p-4 sm:p-6 md:p-8">
            <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-white">
              {current ? (
                <img
                  src={current}
                  alt=""
                  data-testid="product-library-main-image"
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="h-40 w-40 rounded-xl bg-white/70" />
              )}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label={t("productLibrary.previousImage")}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() =>
                      setIndex((currentIndex) =>
                        currentIndex === 0
                          ? images.length - 1
                          : currentIndex - 1,
                      )
                    }
                    className="absolute left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#111] shadow-sm ring-1 ring-black/5 hover:-translate-y-0.5 hover:bg-white"
                  >
                    <FiChevronLeft size={20} />
                  </button>
                  <button
                    type="button"
                    aria-label={t("productLibrary.nextImage")}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() =>
                      setIndex(
                        (currentIndex) => (currentIndex + 1) % images.length,
                      )
                    }
                    className="absolute right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#111] shadow-sm ring-1 ring-black/5 hover:-translate-y-0.5 hover:bg-white"
                  >
                    <FiChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1 sm:mt-5 sm:gap-3">
                {images.map((image, imageIndex) => (
                  <button
                    key={`${image}-${imageIndex}`}
                    type="button"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() => setIndex(imageIndex)}
                    aria-label={t("productLibrary.viewImage", { index: imageIndex + 1 })}
                    className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg sm:h-16 sm:w-16 md:h-20 md:w-20 ${
                      imageIndex === index
                        ? "ring-2 ring-[#111] shadow-sm"
                        : "ring-1 ring-black/10 hover:ring-black/25"
                    }`}
                  >
                    <img
                      src={image}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </section>
          <section className="flex min-w-0 flex-col overflow-y-auto p-4 sm:p-6 md:p-8 lg:p-10">
            <h2 className="pr-10 text-xl font-semibold leading-tight tracking-[-0.01em] text-[#111] sm:text-2xl md:text-[28px] lg:text-[32px]">
              {displayName}
            </h2>
            {product.description && (
              <p className="mt-3 max-w-prose text-sm leading-relaxed text-[#3A3A3A] sm:mt-4 sm:text-[15px]">
                {product.description}
              </p>
            )}
            <div className="mt-4 space-y-1.5 text-[13px] text-[#2C2C2C] sm:mt-5 sm:space-y-2 sm:text-[14px]">
              {materials.length > 0 && (
                <p>
                  <span className="font-medium text-[#111]">{t("common.materials")}</span> ·{" "}
                  {materials.join(", ")}
                </p>
              )}
              {modelDimensions && (
                <p>
                  <span className="font-medium text-[#111]">{t("common.objectSize3d")}</span>
                  {" · "}
                  {t("common.widthAbbr")}{" "}
                  {millimeterDimensionValue(modelDimensions.width)} /{" "}
                  {t("common.depthAbbr")}{" "}
                  {millimeterDimensionValue(modelDimensions.depth)} /{" "}
                  {t("common.heightAbbr")}{" "}
                  {millimeterDimensionValue(modelDimensions.height)}
                </p>
              )}
              <p>
                <span className="font-medium text-[#111]">{t("productLibrary.productFeed")}</span>
                {" · "}
                {t("common.widthAbbr")}{" "}
                {millimeterDimensionValue(feedDimensions.width)} /{" "}
                {t("common.depthAbbr")}{" "}
                {millimeterDimensionValue(feedDimensions.depth)} /{" "}
                {t("common.heightAbbr")}{" "}
                {millimeterDimensionValue(feedDimensions.height)}
              </p>
              {(product.brand || product.supplier?.name) && (
                <p className="text-[#595959]">
                  {[product.brand, product.supplier?.name]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
            </div>
            <div className="mt-6 flex-1 border-t border-black/10 pt-4 sm:mt-8 sm:pt-6">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <p className="text-xl font-semibold tracking-[-0.01em] text-[#111] sm:text-[24px] md:text-[28px]">
                  {formatDisplayPrice(
                    product.price,
                    displayCurrency,
                    ratesArray,
                    base,
                    i18n.language,
                  ) ?? t("common.priceUnavailable")}
                </p>
                {product.supplier?.logo && (
                  <div className="h-10 w-20 flex-shrink-0 overflow-hidden sm:h-12 sm:w-24 md:h-14 md:w-28">
                    <img
                      src={product.supplier.logo}
                      alt=""
                      className="h-full w-full object-contain"
                    />
                  </div>
                )}
              </div>
              {product.url && (
                <a
                  href={product.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 flex min-w-0 items-center justify-center rounded-xl bg-[#111] px-4 py-3.5 text-sm font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-[#1e1e1e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
                >
                  {t("productLibrary.viewAtRetailer")}
                </a>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
});
